// The input/output blocks of a stored analysis trace, per step. Single source of
// truth for both the Data tab (AnalysisTrace renders one DataBlock per entry) and
// the zip bundle (one file per entry) — so the zip always holds exactly what the
// per-block ↓ buttons download, for every engine shape.
import type { DebugStep2, DebugTrace } from 'types/analysis';

export type BlockKind = 'json' | 'text' | 'markdown';

export interface TraceBlock {
	/** Stable id, also the file stem: `step1_input_data` → `<tag>_step1_input_data.json`. */
	key: string;
	label: string;
	kind: BlockKind;
	value: unknown;
	defaultOpen?: boolean;
}

export type Step2Shape = 'enforcer' | 'sandbox' | 'incontext';

export interface TraceBlocks {
	/** null for single-step engines (v0/v0_1/v1), where step1 is the whole run. */
	step2Shape: Step2Shape | null;
	step1: TraceBlock[];
	step2: TraceBlock[];
	step3: TraceBlock[];
}

export function blockText(kind: BlockKind, value: unknown): string {
	return kind === 'json' ? JSON.stringify(value ?? null, null, 2) : String(value ?? '');
}

export function isBlockEmpty(kind: BlockKind, value: unknown): boolean {
	const text = blockText(kind, value);
	return text.trim().length === 0 || text === '{}' || text === 'null';
}

export function blockExtension(kind: BlockKind): string {
	return kind === 'json' ? 'json' : kind === 'markdown' ? 'md' : 'txt';
}

export function step2ShapeOf(step2: DebugStep2): Step2Shape {
	if (step2.blueprint !== undefined || step2.input !== undefined) return 'enforcer';
	if (step2.code !== undefined || step2.results !== undefined) return 'sandbox';
	return 'incontext'; // v3: Claude calcs + writes in one call
}

/** Present-and-non-null — for blocks the Data tab only shows when the run stored them. */
const has = (v: unknown) => v !== undefined && v !== null;

/** LTF/HTF companions of step1.data. Most rows carry `{}`, so only non-empty ones get a block. */
function extraDataBlocks(prefix: string, labelBase: string, data2: unknown, data3: unknown): TraceBlock[] {
	const out: TraceBlock[] = [];
	if (!isBlockEmpty('json', data2)) out.push({ key: `${prefix}_2`, label: `${labelBase} (data_2)`, kind: 'json', value: data2 });
	if (!isBlockEmpty('json', data3)) out.push({ key: `${prefix}_3`, label: `${labelBase} (data_3)`, kind: 'json', value: data3 });
	return out;
}

export function traceBlocks(trace: DebugTrace): TraceBlocks {
	const { step1, step2, step3 } = trace;
	const heatmapLabel = `Heatmap data ${step1.heatmap_used ? '(injected)' : '(not used)'}`;

	// Single-step engines. Two output shapes: prose (v0/v0_1, and v1 rows written
	// before v1 moved to JSON — they stay in the audit log forever, so detect per
	// row) or a StyleJson object.
	if (!step2) {
		const out = step1.output as { prose?: unknown; format?: unknown } | undefined;
		const isProse = out?.format === 'prose' || typeof out?.prose === 'string';
		const blocks: TraceBlock[] = [];
		if (has(step1.doc_file_ids)) blocks.push({ key: 'step1_input_reference_docs', label: 'Input · reference docs (Files API)', kind: 'json', value: step1.doc_file_ids });
		// v1 sends worker data as text (no image) — what Claude received.
		if (has(step1.data)) blocks.push({ key: 'step1_input_data', label: 'Input · data sent to Claude', kind: 'json', value: step1.data });
		blocks.push(...extraDataBlocks('step1_input_data', 'Input · data sent to Claude', step1.data_2, step1.data_3));
		if (has(step1.heatmap_data)) blocks.push({ key: 'step1_input_heatmap', label: heatmapLabel, kind: 'json', value: step1.heatmap_data });
		if (isProse) {
			const prose = step1.raw_output ?? String(out?.prose ?? '');
			blocks.push({ key: 'step1_output_analysis', label: 'Output · analysis (rendered)', kind: 'markdown', value: prose, defaultOpen: true });
		} else {
			blocks.push({ key: 'step1_output_analysis', label: 'Output · analysis JSON', kind: 'json', value: step1.output, defaultOpen: true });
			// The pre-parse model text — worth seeing when a run needed the reformat retry.
			if (has(step1.raw_output)) blocks.push({ key: 'step1_output_raw', label: 'Output · raw text', kind: 'text', value: step1.raw_output });
		}
		return { step2Shape: null, step1: blocks, step2: [], step3: [] };
	}

	const shape = step2ShapeOf(step2);

	const s1: TraceBlock[] = [
		{ key: 'step1_output_data', label: 'Output · data used', kind: 'json', value: step1.data, defaultOpen: true },
		...extraDataBlocks('step1_output_data', 'Output · data used', step1.data_2, step1.data_3),
		{ key: 'step1_output_heatmap', label: heatmapLabel, kind: 'json', value: step1.heatmap_data },
	];

	const s2: TraceBlock[] = [];
	if (shape === 'enforcer') {
		s2.push(
			{ key: 'step2_input_data', label: 'Input · data + mode', kind: 'json', value: step2.input },
			{ key: 'step2_output_blueprint', label: 'Output · blueprint (text → Claude)', kind: 'text', value: step2.blueprint, defaultOpen: true },
		);
	} else if (shape === 'sandbox') {
		const code = Array.isArray(step2.code) ? step2.code.join('\n\n# ─── next block ───\n\n') : (step2.code ?? '');
		const stdout = (step2.results ?? [])
			.map((r) => (typeof r.stdout === 'string' ? r.stdout : ''))
			.filter(Boolean)
			.join('\n\n# ─── next result ───\n\n');
		s2.push(
			{ key: 'step2_input_payload', label: 'Input · payload handed to Claude', kind: 'json', value: step2.trimmed_input },
			{ key: 'step2_code', label: 'Code · python Claude ran', kind: 'text', value: code, defaultOpen: true },
			{ key: 'step2_output_stdout', label: 'Output · stdout', kind: 'text', value: stdout },
			{ key: 'step2_output_results', label: 'Output · full execution results', kind: 'json', value: step2.results },
		);
	} else {
		s2.push({ key: 'step2_input_data', label: 'Input · data sent to Claude', kind: 'json', value: step2.data_sent_to_claude });
		if (has(step2.prompt)) s2.push({ key: 'step2_input_prompt', label: 'Input · prompt', kind: 'text', value: step2.prompt });
		s2.push({ key: 'step2_output_analysis', label: 'Output · analysis JSON', kind: 'json', value: step2.output, defaultOpen: true });
		if (has(step2.raw_output)) s2.push({ key: 'step2_output_raw', label: 'Output · raw text', kind: 'text', value: step2.raw_output });
	}

	const s3: TraceBlock[] = [];
	if (step3) {
		if (has(step3.input_text)) s3.push({ key: 'step3_input_prompt', label: 'Input · prompt sent to Claude', kind: 'text', value: step3.input_text });
		s3.push({ key: 'step3_output_analysis', label: 'Output · analysis JSON', kind: 'json', value: step3.output, defaultOpen: true });
		if (has(step3.raw_output)) s3.push({ key: 'step3_output_raw', label: 'Output · raw text', kind: 'text', value: step3.raw_output });
	}

	return { step2Shape: shape, step1: s1, step2: s2, step3: s3 };
}

/** Step fields a block already exports — everything else lands in the step's details file. */
export const BLOCK_FIELDS: Record<'step1' | 'step2' | 'step3', string[]> = {
	step1: ['doc_file_ids', 'data', 'data_2', 'data_3', 'heatmap_data', 'output', 'raw_output'],
	step2: ['input', 'blueprint', 'trimmed_input', 'code', 'results', 'data_sent_to_claude', 'prompt', 'output', 'raw_output'],
	step3: ['input_text', 'output', 'raw_output'],
};
